import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import type { UserSheetProfile } from "@/components/user-sheet/types";

import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useUpdateProfileUser } from "@/hooks/mutations/useUpdateProfileUser";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";

// Pay columns are integers in the DB, so cents can't be stored yet.
const paySchema = z.union([
  z.null(),
  z.coerce.number().int("Whole dollars only"),
]);

const editUserProfileSchema = z.object({
  currentPay: paySchema,
  desiredPay: paySchema,
  email: z.string(),
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  // TODO: Cleanup null portion, as it's used to make handleSubmit work, but it's messy
  permanentStatus: z.string().or(z.null()),
});

// Fetches the profile and defers the form mount until it's loaded, so the
// form's defaults are seeded from real data instead of blanks.
export const EditUserProfileDialog = ({
  onOpenChange,
  onSuccess,
  open,
  userId,
}: {
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  open: boolean;
  userId: number;
}) => {
  const jobId = useCurrentJobId();
  const { data: sheetData } = useGetCandidacySheetData({ jobId, userId });
  const stafferId = useGetCurrentUser()?.id;

  if (!stafferId || !sheetData) return null;

  return (
    <EditUserProfileDialogLoaded
      onOpenChange={onOpenChange}
      onSuccess={onSuccess}
      open={open}
      sheetData={sheetData}
      stafferId={stafferId}
      userId={userId}
    />
  );
};

const EditUserProfileDialogLoaded = ({
  onOpenChange,
  onSuccess,
  open,
  sheetData,
  stafferId,
  userId,
}: {
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  open: boolean;
  sheetData: UserSheetProfile;
  stafferId: number;
  userId: number;
}) => {
  const { mutate: updateProfileUser, isPending: isPendingUpdateProfileUser } =
    useUpdateProfileUser();
  const jobId = useCurrentJobId();
  const novaUtils = novaClient.useUtils();

  const form = useForm<z.infer<typeof editUserProfileSchema>>({
    resolver: zodResolver(editUserProfileSchema),
    // Nova types these as non-optional but omits unset fields at runtime, so
    // coerce through a loosened view rather than trusting the wire type.
    defaultValues: {
      currentPay:
        (sheetData.profile as { currentPay?: number }).currentPay ?? null,
      desiredPay:
        (sheetData.profile as { desiredPay?: number }).desiredPay ?? null,
      // Email will only be shown when missing, so no need to default it to current email
      email: "",
      firstName: (sheetData.profile as { firstName?: string }).firstName ?? "",
      lastName: (sheetData.profile as { lastName?: string }).lastName ?? "",
      permanentStatus:
        (sheetData as { permanentStatus?: string }).permanentStatus ?? "",
    },
  });

  const handleSubmit = (data: z.infer<typeof editUserProfileSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Update user profile",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      {
        has_current_pay: data.currentPay !== null,
        has_desired_pay: data.desiredPay !== null,
        has_email: !!data.email,
        has_permanent_status: !!data.permanentStatus,
      },
    );
    // Same loosened view as the defaults — a name nova omitted must compare
    // as "" so an untouched field stays out of the request.
    const originalFirstName =
      (sheetData.profile as { firstName?: string }).firstName ?? "";
    const originalLastName =
      (sheetData.profile as { lastName?: string }).lastName ?? "";
    const updateRequest = {
      currentPay: data.currentPay,
      desiredPay: data.desiredPay,
      // Prevents clearing email for those emails that are already set.
      ...(data.email && { email: data.email }),
      // Only send names when the staffer actually changed them.
      ...(data.firstName !== originalFirstName && {
        firstName: data.firstName,
      }),
      ...(data.lastName !== originalLastName && {
        lastName: data.lastName,
      }),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      permanentStatus: data.permanentStatus || null,
    };

    const handleSuccess = (
      updatedData: z.infer<typeof editUserProfileSchema>,
    ) => {
      form.reset(updatedData);
      // The whole sheet renders from this query; names/email/permanentStatus
      // aren't synced at the mutation-hook level, so refresh it here.
      void novaUtils.internal.user.active.getCandidacySheetData.invalidate({
        jobId,
        userId,
      });
      onSuccess();
    };

    updateProfileUser(
      {
        stafferId,
        data: {
          ...updateRequest,
          permanentStatus: updateRequest.permanentStatus,
        },
        userId,
      },
      {
        onSuccess: (profile) => {
          handleSuccess({
            currentPay: profile.currentPay ?? null,
            desiredPay: profile.desiredPay ?? null,
            email: profile.email ?? "",
            firstName: profile.firstName,
            lastName: profile.lastName,
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            permanentStatus: profile.permanentStatus || null,
          });
        },
      },
    );
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        form.reset();
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Edit User Profile
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            onKeyDown={(event) => {
              if (event.metaKey && event.key === "Enter") {
                event.preventDefault();
                void form.handleSubmit(handleSubmit)();
              }
            }}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <Column className="gap-4">
              {/* First Name */}
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="First name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Last Name */}
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Current Pay */}
              <FormField
                control={form.control}
                name="currentPay"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Current Pay (optional)</FormLabel>
                    <FormControl>
                      <Input
                        onChange={(event) => {
                          // Whole dollars only — strips cents and minus signs
                          // as they're typed rather than failing on submit.
                          const wholeDollars = event.target.value.replace(
                            /\D/g,
                            "",
                          );
                          field.onChange(
                            wholeDollars === "" ? null : wholeDollars,
                          );
                        }}
                        placeholder="Current pay"
                        step={1}
                        type="number"
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Desired Pay */}
              <FormField
                control={form.control}
                name="desiredPay"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Desired Pay (optional)</FormLabel>
                    <FormControl>
                      <Input
                        onChange={(event) => {
                          // Whole dollars only — strips cents and minus signs
                          // as they're typed rather than failing on submit.
                          const wholeDollars = event.target.value.replace(
                            /\D/g,
                            "",
                          );
                          field.onChange(
                            wholeDollars === "" ? null : wholeDollars,
                          );
                        }}
                        placeholder="Desired pay"
                        step={1}
                        type="number"
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email */}
              {!sheetData.email && (
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="text-slate-700">
                      <FormLabel>Email (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Email" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Permananent Status */}
              <FormField
                control={form.control}
                name="permanentStatus"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Permanent Status (optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter a permanent status"
                        {...field}
                        // TODO: Cleanup this along with zod schema
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Column>
            <Row className="justify-end gap-2">
              <Button
                className="rounded-full"
                onClick={() => {
                  onOpenChange(false);
                }}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                className="rounded-full"
                disabled={isPendingUpdateProfileUser}
                type="submit"
              >
                {isPendingUpdateProfileUser
                  ? "Updating..."
                  : "Update user profile"}
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
